/**
 * Authentication Store
 * Verwaltet Benutzer-Authentifizierung und Session
 */

import { writable, derived, get } from 'svelte/store';
import type { User, AuthToken } from '../types';
import { authStorage } from '../services/storage';
import { frappeClient } from '../services/frappe';

interface AuthState {
  user: User | null;
  token: AuthToken | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

function createAuthStore() {
  // Initial State aus LocalStorage laden
  const initialUser = authStorage.loadUser();
  const initialToken = authStorage.loadToken();

  // KEIN Fallback mehr auf Environment-Variable
  // Benutzer muss sich immer einloggen

  const { subscribe, set, update } = writable<AuthState>({
    user: initialUser,
    token: initialToken,
    isAuthenticated: !!initialToken && !!initialUser,
    isLoading: false,
    error: null
  });

  return {
    subscribe,

    /**
     * Login mit Username/Password
     */
    async login(username: string, password: string): Promise<void> {
      update((state) => ({ ...state, isLoading: true, error: null }));

      try {
        const response = await frappeClient.login(username, password);

        // Token erstellen (nur für lokalen Auth-Status, API-Anfragen nutzen API-Keys aus Settings)
        const sessionToken = `session-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const token: AuthToken = {
          access_token: sessionToken,
          refresh_token: sessionToken,
          expires_at: Date.now() + 24 * 60 * 60 * 1000 // 24 Stunden (Session läuft ab)
        };

        const user: User = {
          id: username,
          name: username,
          email: username,
          full_name: response.full_name || username
        };

        // Speichern
        authStorage.saveToken(token);
        authStorage.saveUser(user);

        // Store aktualisieren
        set({
          user,
          token,
          isAuthenticated: true,
          isLoading: false,
          error: null
        });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Login fehlgeschlagen';
        update((state) => ({
          ...state,
          isLoading: false,
          error: errorMessage
        }));
        throw error;
      }
    },

    /**
     * Logout
     */
    logout(): void {
      authStorage.clearToken();
      set({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      });
    },

    /**
     * Prüft ob Token noch gültig ist
     */
    isTokenValid(): boolean {
      const state = get({ subscribe });
      if (!state.token) return false;
      return state.token.expires_at > Date.now();
    },

    /**
     * Aktualisiert User-Daten
     */
    updateUser(user: Partial<User>): void {
      update((state) => {
        if (state.user) {
          const updatedUser = { ...state.user, ...user };
          authStorage.saveUser(updatedUser);
          return { ...state, user: updatedUser };
        }
        return state;
      });
    },

    /**
     * Setzt Fehler
     */
    setError(error: string | null): void {
      update((state) => ({ ...state, error }));
    },

    /**
     * Setzt Loading-Status
     */
    setLoading(loading: boolean): void {
      update((state) => ({ ...state, isLoading: loading }));
    },

    /**
     * Erneuert die Session durch Ping-Request
     * Wird automatisch aufgerufen, bevor die Session abläuft
     *
     * Frappe verlängert die Session automatisch bei jedem erfolgreichen API-Request
     */
    async refreshSession(): Promise<void> {
      const state = get({ subscribe });
      if (!state.token || !state.isAuthenticated) {
        return;
      }

      // Prüfe ob Session noch gültig ist (mit Puffer von 1 Stunde)
      const oneHourFromNow = Date.now() + 60 * 60 * 1000;
      if (state.token.expires_at > oneHourFromNow) {
        // Session ist noch lange gültig, kein Refresh nötig
        return;
      }

      try {
        // Ping-Request verlängert die Session automatisch auf Frappe-Seite
        const success = await frappeClient.ping();

        if (success) {
          // Token-Ablaufzeit wurde bereits in frappeClient.updateSessionExpiry() aktualisiert
          // Lade aktualisierten Token
          const updatedToken = authStorage.loadToken();
          if (updatedToken) {
            update((state) => ({
              ...state,
              token: updatedToken
            }));
            console.log('Session erfolgreich erneuert');
          }
        } else {
          throw new Error('Ping fehlgeschlagen');
        }
      } catch (error) {
        console.warn('Session-Refresh fehlgeschlagen:', error);
        // Bei Fehler: Session könnte abgelaufen sein
        // Benutzer muss sich neu einloggen
        // Aber nicht automatisch ausloggen - könnte auch Netzwerkproblem sein
      }
    }
  };
}

export const authStore = createAuthStore();

/**
 * Derived Store: Is Authenticated
 */
export const isAuthenticated = derived(authStore, ($auth) => $auth.isAuthenticated);

/**
 * Derived Store: Current User
 */
export const currentUser = derived(authStore, ($auth) => $auth.user);
